import './index.css'

import { useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useMemo, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { useDispatch } from 'react-redux'
import { useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useSessionStorage } from 'usehooks-ts'

import { useNavigate } from 'react-router-dom'
import { HaloLink } from '../../components/HaloLink'
import { useHeaderVisibility } from '../../contexts/HeaderVisibilityContext'
import { usePersonaBySlug } from '../../hooks/apiHooks/personasHooks'
import { useContextById } from '../../hooks/apiHooks/sessionHooks'
import { useSubscriptionDetails } from '../../hooks/apiHooks/subscriptionHooks'
import { useArtifacts } from '../../hooks/storeHooks/useArtifacts'
import { useMentorsState } from '../../hooks/storeHooks/useMentors'
import {
  useAudioVideo,
  useCompositeData,
  useSession,
} from '../../hooks/storeHooks/useSessionState'
import { useMicrophonePermission } from '../../hooks/useMicrophonePermission'
import { useVisualViewportHeight } from '../../hooks/useVisualViewportHeight'
import { setWebRTCStatus } from '../../store/reducers/sessionReducer'
import { SessionMentor } from '../../types/mentor'
import type { OidcUser } from '../../types/oidcUser'
import { isMacOS } from '../../utils/browserDetect'
import { getOrganizationId } from '../../utils/getOrganization'
import { isAdmin } from '../../utils/isAdmin'
import { AvatarOverlay } from './AvatarOverlay'
import { ErrorMessage } from './ErrorMessage'
import { useNonAuthSession } from './hooks/useNonAuthSession'
import { LearningModal } from './LearningModal'
import { PromptView } from './PromptView'
import { Reminder } from './Reminder'
import { Start } from './Start'
const HALO_WINDOW_OPEN_KEY = 'haloWindowOpen'

const SessionPage: React.FC = () => {
  const navigate = useNavigate()

  const { user, isAuthenticated, isLoading: isAuthLoading } = useAuth()
  const { slug } = useParams()
  const [searchParams] = useSearchParams()
  const contextParam = searchParams.get('context')
  const isFree = searchParams.get('free') === 'true'
  const queryClient = useQueryClient()
  const [outOfCreditsShown, setOutOfCreditsShown] = useState(false)
  const dispatch = useDispatch()
  const isMac = isMacOS()

  useVisualViewportHeight()

  const { setSelectedMentor } = useMentorsState()
  const {
    data: mentorFromSlug,
    isError,
    isLoading: isCompLoading,
  } = usePersonaBySlug(slug || '', true, true)
  const { data: contextData, isLoading: isContextLoading } = useContextById(
    contextParam || '',
  )
  const { freeTimeLeft, isFreeSessionRunning, startCountdown } =
    useNonAuthSession()

  const isContextReady = useMemo(
    () =>
      contextData?.data?.status === 'Completed' ||
      contextData?.data?.status === 'ReadyToUse',
    [contextData?.data?.status],
  )
  const { setCompositeData } = useCompositeData()

  const mentorId = useMemo(
    () => mentorFromSlug?.data?.mentorId as string,
    [mentorFromSlug?.data?.mentorId],
  )

  const compStatus = useMemo(
    () => mentorFromSlug?.data.status,
    [mentorFromSlug?.data.status],
  )

  const isCompReady = useMemo(
    () => compStatus === 'Completed' || compStatus === 'ReadyToUse',
    [compStatus],
  )

  const isGenerating = useMemo(
    () =>
      (!isCompLoading && !isCompReady) ||
      (!!contextParam && !isContextLoading && !isContextReady),
    [isCompLoading, isCompReady, isContextReady, contextParam],
  )

  const {
    avatarId,
    personaData,
    secondsBalance,
    setSecondsBalance,
    setCreditBalance,
    setPersonaData,
  } = useSession()
  const { webRTCStatus } = useAudioVideo()

  const { isHeaderVisible } = useHeaderVisibility()
  const [haloWindowOpen] = useSessionStorage<string | null>(
    HALO_WINDOW_OPEN_KEY,
    null,
  )

  const [prevMentorId, setPrevMentorId] = useState(mentorId)
  const [prevSocketId, setPrevSocketId] = useState<string | null>(null)
  // native app states
  const [appLoading, setAppLoading] = useState(true)
  const [isAppOpened, setAppOpened] = useState(false)
  const [userHasApp, setUserHasApp] = useState(false)

  // Check microphone permission
  const { hasPermission: hasMicPermission } = useMicrophonePermission()

  const orgId = getOrganizationId(user as OidcUser)

  const { artifactPanelOpen, setArtifactsList } = useArtifacts()

  const { data: subscriptionData, isLoading } = useSubscriptionDetails(
    {
      organization_id: orgId,
      persona_id: mentorId,
    },
    isAuthenticated,
  )

  const handlePageChange = () => {
    const freeParam = isFree ? 'free=true' : ''
    const contextParams = contextParam ? `context=${contextParam}` : ''

    const params = [contextParams, freeParam].filter(Boolean).join('&')

    navigate(`${location.pathname}${params ? `?${params}` : ''}`)
  }

  useEffect(() => {
    return () => {
      setSelectedMentor(null)
    }
  }, [isAuthLoading])

  useEffect(() => {
    queryClient.removeQueries({ queryKey: ['getSubscriptionDetails'] })
    document.body.classList.add('session-page')

    return () => {
      document.body.classList.remove('session-page')
    }
  }, [])

  useEffect(() => {
    if (subscriptionData) {
      const { credit_usage } = subscriptionData
      const balance =
        credit_usage.subscription_details.balance +
        credit_usage.pack_details.balance
      const creditBalance =
        credit_usage.subscription_details.creditBalance +
        credit_usage.pack_details.creditBalance
      setSecondsBalance(balance)
      setCreditBalance(creditBalance)
      if (balance === 0) {
        handlePageChange()
      }
    }
  }, [subscriptionData])

  useEffect(() => {
    if (!isAuthenticated && freeTimeLeft === 0 && !isAuthLoading) {
      handlePageChange()
    }
  }, [isAuthenticated, freeTimeLeft, isAuthLoading])

  useEffect(() => {
    if (isContextReady && contextData?.data.prompt) {
      setCompositeData(contextData.data.prompt)
    }
    if (isContextReady && !contextData?.data.prompt) {
      dispatch(
        setWebRTCStatus({
          blocked: true,
          reason: 'No prompt in context',
          message:
            'The watch session is not available yet. Please try again later.',
        }),
      )
    }
  }, [isContextReady, contextData?.data.prompt])

  useEffect(() => {
    if (
      compStatus === 'Failed' ||
      compStatus === 'WaitingForApproval' ||
      isError
    ) {
      dispatch(
        setWebRTCStatus({
          blocked: true,
          reason: 'Mentor status failed',
          btnType: 'home',
          message:
            "Looks like we couldn't spin up your Napster Companion just now. Hang tight and try again soon.",
        }),
      )
    }
  }, [compStatus, isError])

  useEffect(() => {
    if (!appLoading && !isAppOpened && !userHasApp && isMac) {
      const toastId = toast(
        () => (
          <div className="flex justify-between items-center gap-4 flex-col min-[480px]:flex-row whitespace-nowrap">
            <div>
              <p className="text-sm">Want a smoother experience?</p>
              <p className="text-sm">Continue in the macOS app.</p>
            </div>
            <a
              href="https://www.napster.ai/apps"
              target="_blank"
              className="btn btn-secondary btn-small "
            >
              Open in App
            </a>
          </div>
        ),
        {
          autoClose: 10000,
          position: 'top-right',
          hideProgressBar: true,
          closeButton: false,
          closeOnClick: true,
          icon: false,
        },
      )
      return () => {
        toast.dismiss(toastId)
      }
    }
  }, [appLoading, isAppOpened, userHasApp])

  useEffect(() => {
    if (mentorId) {
      setPersonaData({
        ...(mentorFromSlug?.data || {}),
        id: mentorId,
        avatar_id: mentorFromSlug?.data?.avatar?.id,
      } as SessionMentor)
      if (mentorFromSlug?.data.artifacts) {
        setArtifactsList(mentorFromSlug?.data.artifacts)
      }
    } else {
      setPersonaData(null)
    }
  }, [isCompReady, mentorId])

  useEffect(() => {
    if (haloWindowOpen === mentorId) {
      setAppLoading(false)
      setUserHasApp(true)
    }
  }, [haloWindowOpen, mentorId])

  const handleOpenApp = (isOpened: boolean) => {
    setAppOpened(isOpened)
    setAppLoading(false)
    if (isOpened) {
      setUserHasApp(true)
      handlePageChange()
    }
  }

  return (
    <div
      className={`main-container ${artifactPanelOpen ? 'open-artifact' : ''} ${
        isHeaderVisible ? '' : 'header-visible'
      }`}
    >
      {user?.profile.sub &&
        mentorFromSlug?.data.userId &&
        user.profile.sub === mentorFromSlug.data.userId && <LearningModal />}
      <div className="main-wrapper">
        <Start
          isLoading={isLoading && isCompLoading}
          mentorId={mentorId}
          outOfCreditsShown={outOfCreditsShown}
          isGenerating={isGenerating}
          freeTimeLeft={freeTimeLeft}
          isAppOpened={isAppOpened}
          setAppOpened={setAppOpened}
        />
        {personaData !== null &&
          avatarId &&
          !isAuthLoading &&
          !isLoading &&
          !isCompLoading &&
          (isContextReady || !contextParam) &&
          isCompReady &&
          (searchParams.get('free') === 'true' ||
            (searchParams.get('start') === 'true' &&
              isAuthenticated &&
              secondsBalance > 0)) &&
          !appLoading &&
          !isAppOpened &&
          hasMicPermission && (
            <AvatarOverlay
              personaData={personaData}
              mentorId={mentorId || ''}
              prevMentorId={prevMentorId || ''}
              prevSocketId={prevSocketId}
              freeTimeLeft={freeTimeLeft}
              contextData={contextData?.data}
              isFreeSessionRunning={isFreeSessionRunning}
              setPrevMentorId={setPrevMentorId}
              setPrevSocketId={setPrevSocketId}
              startCountdown={startCountdown}
            />
          )}
        {isAdmin(user) && (
          <PromptView mentorId={mentorId} personaData={personaData} />
        )}
        {webRTCStatus.blocked && webRTCStatus.errorStatus !== 400 && (
          <ErrorMessage />
        )}
        {!isAuthLoading && (
          <Reminder
            isHidden={artifactPanelOpen}
            secondsBalance={isAuthenticated ? secondsBalance : freeTimeLeft}
            setOutOfCreditsShown={setOutOfCreditsShown}
            isSocketConnected={
              isAuthenticated ? !isLoading : isFreeSessionRunning
            }
          />
        )}
      </div>
      {mentorId && appLoading && (
        <HaloLink
          mentorId={mentorId}
          showLink={false}
          onLoad={handleOpenApp}
          autoClick
        />
      )}
    </div>
  )
}

export default SessionPage
